import React, { useState, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Container,
  Paper,
  Button,
  Box,
  TextField,
  Typography,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  IconButton,
  InputAdornment,
  Tooltip,
  Zoom,
  Fade,
  Select,
  MenuItem,
  InputLabel,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Preview as PreviewIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../components/context/AuthContext';
import * as XLSX from 'xlsx';
import ComposeForm from '../components/ComposeForm';
import emailTemplates from '../components/EmailTemplates';

// Custom MUI Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
      dark: '#5a6fd8',
    },
    secondary: {
      main: '#764ba2',
      dark: '#6a4190',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937',
      secondary: '#4b5563',
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    h1: { fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.2 },
    h5: { fontSize: '1.5rem', fontWeight: 600 },
    h6: { fontSize: '1.25rem', fontWeight: 600 },
    body1: { fontSize: '0.95rem', lineHeight: 1.6 },
    body2: { fontSize: '0.85rem', lineHeight: 1.5 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          padding: '8px 20px',
          fontWeight: 600,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          border: '1px solid rgba(102, 126, 234, 0.2)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(102, 126, 234, 0.2)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
        },
      },
    },
  },
});

// Quill editor configuration
const modules = {
  toolbar: {
    container: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
    handlers: {
      image: function () {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        input.onchange = async () => {
          const file = input.files[0];
          if (file) {
            if (file.size > 5 * 1024 * 1024) {
              alert('Image size exceeds 5MB limit.');
              return;
            }
            const resizedImage = await resizeImage(file, { maxWidth: 800, maxHeight: 600 });
            const quill = this.quill;
            const range = quill.getSelection();
            quill.insertEmbed(range.index, 'image', resizedImage, 'user');
            quill.setSelection(range.index + 1, 0);
          }
        };
      }
    }
  },
  clipboard: {
    matchVisual: false,
  }
};

const formats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'align',
  'link', 'image'
];

// Helper function to resize images
const resizeImage = (file, options) => {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { maxWidth, maxHeight } = options;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL(file.type, 0.8));
      };
    };
    reader.readAsDataURL(file);
  });
};

// Subcomponents
const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} role="tabpanel" id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`}>
    {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
  </div>
);

const RecipientsForm = ({
  recipientType, setRecipientType,
  newRecipient, setNewRecipient,
  recipients, setRecipients,
  fileInputRef, uploadedFile, setUploadedFile,
  setError, setSuccess, clearSendAlerts
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const handleAddRecipient = useCallback(() => {
    clearSendAlerts();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (newRecipient.name && newRecipient.email && emailRegex.test(newRecipient.email)) {
      if (recipients.find(r => r.email === newRecipient.email)) {
        setError('Email already exists in the list');
        return;
      }
      setRecipients([...recipients, { ...newRecipient, id: Date.now() }]);
      setNewRecipient({ name: '', email: '' });
      setError('');
      setSuccess('Successfully added recipient');
    } else {
      setError('Please enter a valid name and email');
    }
  }, [newRecipient, recipients, setRecipients, setNewRecipient, setError, setSuccess, clearSendAlerts]);

  const handleRemoveRecipient = useCallback((id) => {
    clearSendAlerts();
    setRecipients(recipients.filter(r => r.id !== id));
  }, [recipients, setRecipients, clearSendAlerts]);

  const handleClearAll = () => {
    setRecipients([]);
    setUploadedFile(null);
    setSuccess('All recipients cleared');
  };

  const handleFileUpload = (event) => {
    clearSendAlerts();
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/octet-stream'
      ];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!validTypes.includes(file.type) && !['xlsx', 'xls'].includes(fileExtension)) {
        setError('Invalid file type. Please upload an Excel (.xlsx or .xls) file.');
        return;
      }
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        setError('File is too large. Maximum allowed size is 2MB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          if (jsonData.length === 0) {
            setError('Excel file is empty.');
            return;
          }
          const firstRow = jsonData[0];
          const hasName = 'Name' in firstRow || 'name' in firstRow;
          const hasEmail = 'Email' in firstRow || 'email' in firstRow;
          if (!hasName || !hasEmail) {
            setError('Missing required columns. Please ensure the Excel file has both "Name" and "Email" columns.');
            return;
          }
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const formattedRecipients = jsonData
            .map((row, index) => ({
              id: Date.now() + index,
              name: row.Name || row.name || '',
              email: row.Email || row.email || '',
              mobile: row.Mobile || row.mobile || row.phone || ''
            }))
            .filter(r => r.email && emailRegex.test(r.email));
          setRecipients(formattedRecipients);
          setUploadedFile(file.name);
          setError('');
          setSuccess(`Successfully imported ${formattedRecipients.length} recipients`);
        } catch (err) {
          setError('Error reading file. Please ensure it\'s a valid Excel file.');
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const filteredRecipients = recipients.filter(r =>
    r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Fade in timeout={800}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
          Manage Recipients
        </Typography>
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Add Recipients
          </FormLabel>
          <RadioGroup
            row
            value={recipientType}
            onChange={e => setRecipientType(e.target.value)}
            sx={{ gap: { xs: 1, sm: 2 } }}
          >
            <FormControlLabel value="manual" control={<Radio size="small" />} label="Manual Entry" />
            <FormControlLabel value="upload" control={<Radio size="small" />} label="Upload Excel" />
          </RadioGroup>
        </FormControl>
        {recipientType === 'manual' ? (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Name"
                value={newRecipient.name}
                onChange={e => setNewRecipient({ ...newRecipient, name: e.target.value })}
                variant="outlined"
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newRecipient.email}
                onChange={e => setNewRecipient({ ...newRecipient, email: e.target.value })}
                variant="outlined"
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAddRecipient}
                startIcon={<AddIcon />}
                sx={{
                  height: '40px',
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                }}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ mb: 3 }}>
            <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} ref={fileInputRef} style={{ display: 'none' }} />
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              startIcon={<UploadIcon />}
              sx={{
                mb: 2,
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.1)' },
              }}
            >
              Upload Excel File
            </Button>
            {uploadedFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Uploaded: {uploadedFile}
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Recipients List ({recipients.length})
          </Typography>
          {recipients.length > 0 && (
            <Button
              variant="text"
              color="error"
              onClick={handleClearAll}
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, fontWeight: 600 }}
            >
              Clear All
            </Button>
          )}
        </Box>
        <TextField
          fullWidth
          label="Search Recipients"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          size="small"
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
        />
        {filteredRecipients.length > 0 ? (
          <Card sx={{ maxHeight: 300, overflowY: 'auto' }}>
            <List>
              {filteredRecipients.map((recipient, index) => (
                <ListItem
                  key={recipient.id}
                  divider
                  sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.05)' }, transition: 'background-color 0.2s' }}
                >
                  <ListItemText
                    primary={recipient.name}
                    secondary={recipient.email}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: { xs: '0.9rem', sm: '1rem' } }}
                    secondaryTypographyProps={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                  />
                  <Tooltip title="Remove Recipient">
                    <IconButton edge="end" onClick={() => handleRemoveRecipient(recipient.id)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
          </Card>
        ) : (
          <Typography color="text.secondary">No recipients match the search criteria.</Typography>
        )}
      </Box>
    </Fade>
  );
};

const ResultsPanel = ({ results }) => (
  <Fade in timeout={800}>
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
        Sending Results
      </Typography>
      {results ? (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Campaign Summary
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                label={`Total: ${results.summary.total}`}
                color="primary"
                variant="outlined"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
              />
              <Chip
                label={`Success: ${results.summary.success}`}
                color="success"
                variant="outlined"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
              />
              <Chip
                label={`Failed: ${results.summary.failed}`}
                color="error"
                variant="outlined"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
              />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Detailed Results
            </Typography>
            <Card sx={{ maxHeight: 300, overflowY: 'auto' }}>
              <List>
                {results.results.map((result, index) => (
                  <ListItem
                    key={index}
                    sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.05)' } }}
                  >
                    <ListItemText
                      primary={result.to}
                      secondary={result.status === 'sent' ? 'Successfully sent' : `Failed: ${result.error}`}
                      primaryTypographyProps={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                      secondaryTypographyProps={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                    />
                    <Chip
                      label={result.status}
                      color={result.status === 'sent' ? 'success' : 'error'}
                      size="small"
                      variant="outlined"
                    />
                  </ListItem>
                ))}
              </List>
            </Card>
          </CardContent>
        </Card>
      ) : (
        <Typography color="text.secondary">No results yet. Send an email campaign to see results here.</Typography>
      )}
    </Box>
  </Fade>
);

// Main Component
const EmailSenderPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [subject, setSubject] = useState('');
  const [editorHtml, setEditorHtml] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState({ name: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [recipientType, setRecipientType] = useState('manual');
  const [uploadedFile, setUploadedFile] = useState(null);
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const clearSendAlerts = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSendEmails = async () => {
    setConfirmSendOpen(true);
  };

  const confirmSend = async () => {
    setConfirmSendOpen(false);
    setSuccess('');
    if (!subject || !editorHtml || recipients.length === 0) {
      setError('Please fill in all required fields and add at least one recipient');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const response = await fetch('/send-emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          recipients: recipients.map(r => ({
            name: r.name,
            email: r.email,
            mobile: r.mobile || ''
          })),
          subject,
          message: editorHtml.replace(/<[^>]*>/g, ''),
          htmlMessage: editorHtml
        })
      });

      const data = await response.json();
      if (data.success) {
        setResults(data);
        setSuccess(`Successfully sent ${data.summary.success} emails`);
        setSnackbarOpen(true);
        setActiveTab(2);
      } else {
        setError(data.error || 'Failed to send emails');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  const handleTemplateChange = (e) => {
    const templateLabel = e.target.value;
    setSelectedTemplate(templateLabel);
    const template = emailTemplates.find(t => t.label === templateLabel);
    if (template) {
      setSubject(template.subject);
      setEditorHtml(template.content);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, py: 4 }}>
        <Fade in timeout={800}>
          <Alert severity="warning" sx={{ borderRadius: '8px' }}>
            Please log in to access the email sender.
          </Alert>
        </Fade>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          py: { xs: 4, sm: 6 },
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #e0e7ff 0%, #d1fae5 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%)',
            zIndex: 0,
            opacity: 0.6,
          }}
        />
        <Container maxWidth="lg">
          <Fade in timeout={800}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                textAlign: 'center',
                mb: 4,
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
              }}
            >
              Email Campaign Manager
            </Typography>
          </Fade>

          <Paper sx={{ mt: 3, overflow: 'hidden' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Email campaign tabs"
              sx={{
                bgcolor: 'rgba(102, 126, 234, 0.1)',
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  px: { xs: 2, sm: 3 },
                  py: 1.5,
                  '&.Mui-selected': { color: 'primary.main' },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'primary.main',
                  height: 3,
                },
              }}
            >
              <Tab label="Recipients" id="tab-0" aria-controls="tabpanel-0" />
              <Tab label="Compose" id="tab-1" aria-controls="tabpanel-1" />
              <Tab label="Results" id="tab-2" aria-controls="tabpanel-2" />
            </Tabs>

            <TabPanel value={activeTab} index={0}>
              <RecipientsForm
                recipientType={recipientType}
                setRecipientType={setRecipientType}
                newRecipient={newRecipient}
                setNewRecipient={setNewRecipient}
                recipients={recipients}
                setRecipients={setRecipients}
                fileInputRef={fileInputRef}
                uploadedFile={uploadedFile}
                setUploadedFile={setUploadedFile}
                setError={setError}
                setSuccess={setSuccess}
                clearSendAlerts={clearSendAlerts}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <ComposeForm
                subject={subject}
                setSubject={setSubject}
                editorHtml={editorHtml}
                setEditorHtml={setEditorHtml}
                quillRef={quillRef}
                handlePreview={handlePreview}
                handleSendEmails={handleSendEmails}
                loading={loading}
                recipients={recipients}
                selectedTemplate={selectedTemplate}
                handleTemplateChange={handleTemplateChange}
                emailTemplates={emailTemplates}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <ResultsPanel results={results} />
            </TabPanel>
          </Paper>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 3,
              px: { xs: 2, sm: 3 },
              py: 2,
              backgroundColor: 'rgba(102, 126, 234, 0.05)',
              borderRadius: '8px',
              border: '1px solid rgba(102, 126, 234, 0.2)',
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setActiveTab(activeTab - 1)}
              disabled={activeTab === 0}
              sx={{
                minWidth: { xs: 100, sm: 120 },
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.1)' },
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => setActiveTab(activeTab + 1)}
              disabled={activeTab === 2 || (activeTab === 0 && recipients.length === 0) || (activeTab === 1 && (!subject || !editorHtml))}
              sx={{
                minWidth: { xs: 100, sm: 120 },
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
              }}
            >
              {activeTab === 2 ? 'Finish' : 'Next'}
            </Button>
          </Box>

          {(error || success) && (
            <Zoom in={true}>
              <Alert severity={success ? 'success' : 'error'} sx={{ mt: 2, borderRadius: '8px' }}>
                {success || error}
              </Alert>
            </Zoom>
          )}

          <Dialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            maxWidth="md"
            fullWidth
            fullScreen={window.innerWidth < 600}
            aria-labelledby="preview-dialog-title"
          >
            <DialogTitle
              id="preview-dialog-title"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'rgba(102, 126, 234, 0.1)',
                fontWeight: 600,
              }}
            >
              Preview Email
              <IconButton onClick={() => setPreviewOpen(false)} aria-label="Close preview">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
                Subject: {subject}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ '& img': { maxWidth: '100%', height: 'auto' } }} dangerouslySetInnerHTML={{ __html: editorHtml }} />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setPreviewOpen(false)}
                sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, color: 'primary.main' }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={confirmSendOpen}
            onClose={() => setConfirmSendOpen(false)}
            maxWidth="xs"
            fullWidth
            fullScreen={window.innerWidth < 600}
            aria-labelledby="confirm-send-dialog-title"
          >
            <DialogTitle
              id="confirm-send-dialog-title"
              sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', fontWeight: 600 }}
            >
              Confirm Email Sending
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Are you sure you want to send this email to {recipients.length} recipient{recipients.length > 1 ? 's' : ''}?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setConfirmSendOpen(false)}
                sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, color: 'primary.main' }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmSend}
                variant="contained"
                sx={{
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              severity="success"
              sx={{ borderRadius: '8px', bgcolor: 'success.main', color: 'white' }}
              onClose={() => setSnackbarOpen(false)}
            >
              {success}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default EmailSenderPage;